import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { IconArrowLeft } from "../components/BenchmarkIcons";
import { StarRating } from "../components/StarRating";
import {
  fetchPublicAppBySlug,
  requestAppDownload,
  type PublicAppDetail,
  type PublicAppScreenshot,
} from "../services/marketplace";

/*
 * APP INSTALL DETAIL — APPROVED / FROZEN (MASTER STEP 6)
 * Route: /apps/:slug — single reusable template for all published apps.
 * Data: dynamic by slug via marketplace API. Layout: app-detail.css (frozen).
 */

function formatFileSize(bytes: number | null): string {
  if (!bytes || bytes <= 0) {
    return "—";
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDownloads(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)} M`;
  }

  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)} K`;
  }

  return String(count);
}

type InstallMetaProps = {
  label: string;
  value: string | number;
};

function InstallMeta({ label, value }: InstallMetaProps) {
  return (
    <div className="install-meta-item">
      <div className="install-meta-value">{value}</div>
      <div className="install-meta-label">{label}</div>
    </div>
  );
}

export function AppDetailPage() {
  const { slug = "" } = useParams();
  const [app, setApp] = useState<PublicAppDetail | null>(null);
  const [screenshots, setScreenshots] = useState<PublicAppScreenshot[]>([]);
  const [error, setError] = useState("");
  const [installLoading, setInstallLoading] = useState(false);
  const [installError, setInstallError] = useState("");

  useEffect(() => {
    if (!slug) {
      setError("Application not found.");
      return;
    }

    setError("");
    setInstallError("");
    void fetchPublicAppBySlug(slug)
      .then((result) => {
        setApp(result.app);
        setScreenshots(result.screenshots);
      })
      .catch(() => {
        setApp(null);
        setScreenshots([]);
        setError("Application not found.");
      });
  }, [slug]);

  const handleInstall = async () => {
    if (!app) {
      return;
    }

    setInstallLoading(true);
    setInstallError("");

    try {
      const { downloadUrl, fileName } = await requestAppDownload(app.id);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = fileName ?? "app.apk";
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      setInstallError("Download is unavailable right now. Please try again.");
    } finally {
      setInstallLoading(false);
    }
  };

  if (error) {
    return (
      <article className="install-detail-state">
        <h1 className="install-detail-state__title">App not found</h1>
        <p className="install-detail-state__copy">{error}</p>
        <Link className="oac-btn oac-btn--blue" to="/discover-apps">
          Back to Discover Apps
        </Link>
      </article>
    );
  }

  if (!app) {
    return (
      <article className="install-detail-state">
        <p className="install-detail-state__copy">Loading app details...</p>
      </article>
    );
  }

  const description = app.short_description?.trim() ?? "";
  const visibleScreenshots = screenshots.filter((shot) => shot.url).slice(0, 3);
  const rating = app.average_rating ?? 0;
  const pricing = app.apps_for_sale ? "For sale" : "Free";

  return (
    <section className="install-detail-reference">
      <div className="install-detail-layout">
        <div className="install-detail-main">
          <div className="install-app-header">
            <div className="install-app-icon-wrap">
              {app.icon_url ? (
                <img
                  alt=""
                  className="install-app-icon"
                  src={app.icon_url}
                />
              ) : (
                <div className="install-app-icon-placeholder" />
              )}
            </div>

            <div className="install-app-copy">
              <h1 className="install-app-title">{app.name}</h1>

              <div className="install-rating">
                <StarRating className="install-rating__stars" rating={rating} />
              </div>

              {description ? (
                <div className="install-description">
                  <strong>Description:</strong>
                  <span>{description}</span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="install-phone-row">
            {[0, 1, 2].map((index) => (
              <div className="install-phone-frame" key={index}>
                {visibleScreenshots[index]?.url ? (
                  <img
                    alt={`${app.name} screenshot ${index + 1}`}
                    className="install-phone-image"
                    src={visibleScreenshots[index].url ?? ""}
                  />
                ) : (
                  <div className="install-phone-empty" />
                )}
              </div>
            ))}
          </div>

          <p className="install-back-button">
            <Link
              aria-label="Back to Discover Apps"
              className="oac-installation-steps-page__back-link"
              title="Back to Discover Apps"
              to="/discover-apps"
            >
              <IconArrowLeft
                className="oac-installation-steps-page__back-icon"
                size={40}
              />
            </Link>
          </p>
        </div>

        <aside className="install-meta-column">
          <button
            className="install-primary-button"
            disabled={installLoading}
            onClick={() => void handleInstall()}
            type="button"
          >
            {installLoading ? "Preparing..." : "Install"}
          </button>

          {installError ? (
            <p className="install-detail-error" role="alert">
              {installError}
            </p>
          ) : null}

          <InstallMeta label="Downloads" value={formatDownloads(app.downloads)} />
          <InstallMeta label="Reviews" value={app.reviews.toLocaleString()} />
          <InstallMeta label="Version" value={app.version_number ?? "—"} />
          <InstallMeta label="Category" value={app.category_name ?? "—"} />
          <InstallMeta label="Size" value={formatFileSize(app.file_size_bytes)} />
          <InstallMeta label="Pricing" value={pricing} />
        </aside>
      </div>
    </section>
  );
}
